import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  HeadingText,
  Modal,
  Box,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useGetIsImpersonator } from '@leagueplatform/auth-utils';
import { captureError } from '@leagueplatform/observability';
import { FormattedMessage } from '@leagueplatform/locales';
import { throttle } from 'lodash-es';

interface SessionTimeoutProps {
  timeoutInterval: number;
  getIsAuthenticated: () => Promise<boolean>;
  onSessionExpire: () => void;
  onSessionExtend: () => void;
  forceIdleSessionLogout?: boolean;
}

interface TimeoutModalContentProps {
  onLogout: () => void;
  onExtend: () => void;
}

const DEBOUNCE_MILLIS = 1000;
const SESSION_TIMEOUT_BUFFER = 150000;

const TimeoutModalContent = ({
  onLogout,
  onExtend,
}: TimeoutModalContentProps) => {
  const isImpersonator = useGetIsImpersonator();

  let messageId = 'SESSION_ABOUT_TO_TIMEOUT';
  if (isImpersonator) {
    messageId = 'SESSION_ABOUT_TO_TIMEOUT_IMPERSONATOR';
  }

  return (
    <>
      <Modal.Title>
        <HeadingText css={{ textAlign: 'center' }} level="1" size="lg">
          <FormattedMessage id="SESSION_TIMEOUT" />
        </HeadingText>
      </Modal.Title>
      <UtilityText
        css={{
          marginTop: '$oneAndHalf',
          marginBottom: '$oneAndHalf',
          display: 'block',
        }}
      >
        {/* eslint-disable-next-line @leagueplatform/literal-i18n-message-id */}
        <FormattedMessage id={messageId} />
      </UtilityText>
      <Box css={{ display: 'flex', justifyContent: 'end' }}>
        <Button priority="secondary" onClick={onLogout}>
          <FormattedMessage id="SESSION_LOG_OUT" />
        </Button>
        {!isImpersonator && (
          <Button css={{ marginInlineStart: '$one' }} onClick={onExtend}>
            <FormattedMessage id="CONTINUE_SESSION" />
          </Button>
        )}
      </Box>
    </>
  );
};

export const SessionTimeout = ({
  timeoutInterval,
  getIsAuthenticated,
  onSessionExpire,
  onSessionExtend,
  forceIdleSessionLogout = false,
}: SessionTimeoutProps) => {
  const [showModal, setShowModal] = useState(false);

  /**
   * We will use this broadcast channel to ensure that when the timeout
   * timer is restarted in one browser context, it is propagated to any
   * other contexts (e.g., another tab) so as to prevent the other
   * context from invalidating the auth token due to inactivity.
   */
  const sessionTimeoutBroadcastChannel = useRef<BroadcastChannel | undefined>(
    undefined,
  );

  const promptTimeout = useRef<number>();
  const expiryTimeout = useRef<number>();

  const clearTimeouts = useCallback(() => {
    window.clearTimeout(promptTimeout.current);
    window.clearTimeout(expiryTimeout.current);
  }, []);

  const restartTimer = useCallback(
    (
      /**
       * Whether the source of user activity initiating this
       * timer restart occured in the current browser context (i.e., tab)
       * or in another context.
       */
      browserContextSource: 'self' | 'other',
    ) => {
      clearTimeouts();

      if (browserContextSource === 'self') {
        if (sessionTimeoutBroadcastChannel.current) {
          sessionTimeoutBroadcastChannel.current.postMessage('');
        } else {
          captureError(
            new Error(
              'Error sending session timeout postMessage, broadcast channel is not created.',
            ),
            {
              errorName: 'SessionTimeout - BroadcastChannel Error',
            },
          );
        }
      }

      if (!forceIdleSessionLogout) {
        promptTimeout.current = window.setTimeout(async () => {
          if (await getIsAuthenticated()) {
            setShowModal(true);
          }
        }, timeoutInterval - SESSION_TIMEOUT_BUFFER);
      }

      expiryTimeout.current = window.setTimeout(
        onSessionExpire,
        timeoutInterval,
      );
    },
    [
      clearTimeouts,
      getIsAuthenticated,
      onSessionExpire,
      timeoutInterval,
      forceIdleSessionLogout,
    ],
  );

  useEffect(() => {
    sessionTimeoutBroadcastChannel.current = new BroadcastChannel(
      'league-session-timeout-timer-restart',
    );
    const onInteractionEvent = throttle(() => {
      /**
       * In case this is a `visibilityChange` event, we will do nothing if
       * the visibility has just changed to `hidden`.
       */
      if (document.visibilityState !== 'visible') {
        return;
      }

      restartTimer('self');
    }, DEBOUNCE_MILLIS);

    document.addEventListener('visibilitychange', onInteractionEvent);
    document.addEventListener('click', onInteractionEvent);
    document.addEventListener('keydown', onInteractionEvent);

    /**
     * If we receive a message on the broadcast channel, it means
     * that another browser tab has had user activity and has restarted
     * its timeout timer, so we should do the same for ours.
     */
    sessionTimeoutBroadcastChannel.current!.onmessage = () => {
      restartTimer('other');
    };

    restartTimer('self');

    return () => {
      clearTimeouts();

      document.removeEventListener('visibilitychange', onInteractionEvent);
      document.removeEventListener('click', onInteractionEvent);
      document.removeEventListener('keydown', onInteractionEvent);
      sessionTimeoutBroadcastChannel.current!.close();
    };
  }, [clearTimeouts, restartTimer]);

  const logoutClickHandler = useCallback(() => {
    clearTimeouts();
    setShowModal(false);
    onSessionExpire();
  }, [clearTimeouts, onSessionExpire]);

  const extendSessionClickHandler = useCallback(() => {
    restartTimer('self');
    setShowModal(false);
    onSessionExtend();
  }, [onSessionExtend, restartTimer]);

  return (
    <Modal.Root open={showModal}>
      <Modal.Content>
        <TimeoutModalContent
          onLogout={logoutClickHandler}
          onExtend={extendSessionClickHandler}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
